import { isTaakAfgerond } from './projectMeta';
import { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Tooltip,
  LinearProgress,
  Collapse,
} from '@mui/material';
import { T, Icon, stitchPill, stitchTooltipSlotProps } from './stitch';
import { productionPhases, getPhaseForType } from './projectMeta';
import TaskItem from './TaskItem';


// Grouped Tasks View - groups tasks by deliverable
// Phase Tasks View - Group by production phase, then by deliverable
function PhaseTasksView({ tasks, onTaskClick, onToggleComplete }) {
  const [expandedPhases, setExpandedPhases] = useState({});
  const [copySuccess, setCopySuccess] = useState(null);

  // Group tasks by production phase, then by deliverable within each phase
  const phaseGroups = useMemo(() => {
    const groups = {};

    // Filter out archived tasks
    const activeTasks = tasks.filter(task => {
      const status = task.fields?.Status || '';
      return !status.toLowerCase().includes('gearchiveerd') && !status.toLowerCase().includes('archief');
    });

    activeTasks.forEach(task => {
      const type = task.fields?.Type || '';
      const phase = getPhaseForType(type);
      const phaseName = phase.name;

      // Extract deliverable from task name
      const taskName = task.fields?.NaamTaak || '';
      const parts = taskName.split(' - ');
      let deliverable = 'Overige taken';
      if (parts.length >= 2) {
        deliverable = parts.slice(1).join(' - ').trim();
      }

      // For Postproductie: don't group by Intern/Extern - just show flat list
      const skipDeliverableGrouping = phaseName === 'Postproductie';

      if (!groups[phaseName]) {
        groups[phaseName] = {
          ...phase,
          tasks: [],
          deliverables: {},
          skipDeliverableGrouping,
        };
      }

      // Add to deliverable group within phase (unless skipped)
      if (!skipDeliverableGrouping) {
        if (!groups[phaseName].deliverables[deliverable]) {
          groups[phaseName].deliverables[deliverable] = [];
        }
        groups[phaseName].deliverables[deliverable].push(task);
      }
      groups[phaseName].tasks.push(task);
    });

    // Sort phases by their order in productionPhases
    const phaseOrder = productionPhases.map(p => p.name);
    return Object.values(groups).sort((a, b) => phaseOrder.indexOf(a.name) - phaseOrder.indexOf(b.name));
  }, [tasks]);

  // Copy phase planning to clipboard
  const copyPhaseToClipboard = useCallback((phase) => {
    const lines = [];
    lines.push(`\u{1F4CB} ${phase.name} Planning`);
    lines.push('');

    // Sort tasks by deadline/end date
    const sortedTasks = [...phase.tasks].sort((a, b) => {
      const dateA = a.fields?.Deadline || a.fields?.EndDateUitvoerder || a.fields?.StartDateUitvoerder || '';
      const dateB = b.fields?.Deadline || b.fields?.EndDateUitvoerder || b.fields?.StartDateUitvoerder || '';
      return dateA.localeCompare(dateB);
    });

    sortedTasks.forEach(task => {
      const name = task.fields?.NaamTaak || 'Taak';
      const deadline = task.fields?.Deadline || task.fields?.EndDateUitvoerder;
      const dateStr = deadline ? new Date(deadline).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' }) : '';

      // Insert "Feedback klant" line before V2/DEF tasks
      const nameLower = name.toLowerCase();
      if (/\bv2\b|^def\b|\bdef$/i.test(nameLower)) {
        const feedbackDate = task.fields?.StartDateUitvoerder;
        const feedbackStr = feedbackDate ? new Date(feedbackDate).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' }) : '';
        lines.push(`Feedback klant${feedbackStr ? ` (voor ${feedbackStr})` : ''}`);
      }

      lines.push(`${name}${dateStr ? ` (${dateStr})` : ''}`);
    });

    navigator.clipboard.writeText(lines.join('\n')).then(() => {
      setCopySuccess(phase.name);
      setTimeout(() => setCopySuccess(null), 2000);
    });
  }, []);

  // Initialize expanded state - expand phases with incomplete tasks
  useEffect(() => {
    const initial = {};
    phaseGroups.forEach((phase, idx) => {
      const completedCount = phase.tasks.filter(t => isTaakAfgerond(t)).length;
      const isComplete = completedCount === phase.tasks.length;
      initial[phase.name] = !isComplete && idx < 3; // Expand incomplete phases, max 3
    });
    setExpandedPhases(initial);
  }, [phaseGroups]);

  const togglePhase = (phaseName) => {
    setExpandedPhases(prev => ({ ...prev, [phaseName]: !prev[phaseName] }));
  };

  const getCompletedCount = (taskList) => taskList.filter(t => isTaakAfgerond(t)).length;

  if (phaseGroups.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography sx={{ fontFamily: T.fontBody, color: T.outline }}>Geen taken in fases gevonden</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {phaseGroups.map((phase) => {
        const completedCount = getCompletedCount(phase.tasks);
        const totalCount = phase.tasks.length;
        const progress = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;
        const isComplete = progress === 100;
        const isExpanded = expandedPhases[phase.name] ?? true;

        // Sort deliverables by task count (most tasks first)
        const sortedDeliverables = Object.entries(phase.deliverables)
          .sort((a, b) => b[1].length - a[1].length);
        const hasMultipleDeliverables = sortedDeliverables.length > 1 ||
          (sortedDeliverables.length === 1 && sortedDeliverables[0][0] !== 'Overige taken');

        return (
          <Box
            key={phase.name}
            sx={{
              overflow: 'hidden',
              bgcolor: T.surface,
              boxShadow: T.shadow,
              borderRadius: '0.75rem',
              borderLeft: `4px solid ${phase.color}`,
            }}
          >
            {/* Phase Header - Clickable */}
            <Box
              onClick={() => togglePhase(phase.name)}
              sx={{
                p: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                backgroundColor: `${phase.color}08`,
                borderBottom: isExpanded ? `1px solid ${T.surfaceHigh}` : 'none',
                cursor: 'pointer',
                transition: 'background-color 0.15s',
                '&:hover': { backgroundColor: `${phase.color}12` },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{
                  transform: isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                  transition: 'transform 0.2s',
                  display: 'flex',
                }}>
                  <Icon name="expand_more" size={24} sx={{ color: T.outline }} />
                </Box>
                <Typography fontSize={24}>{phase.icon}</Typography>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontFamily: T.font, fontWeight: 700, color: T.text }}>
                    {phase.name}
                  </Typography>
                  <Typography variant="caption" sx={{ fontFamily: T.fontBody, color: T.outline }}>
                    {completedCount} van {totalCount} taken afgerond
                    {hasMultipleDeliverables && ` \u2022 ${sortedDeliverables.length} deliverables`}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {/* Copy to clipboard button */}
                <Tooltip title={copySuccess === phase.name ? 'Gekopieerd!' : 'Kopieer planning'} slotProps={stitchTooltipSlotProps}>
                  <Box
                    component="span"
                    onClick={(e) => {
                      e.stopPropagation();
                      copyPhaseToClipboard(phase);
                    }}
                    sx={{
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      width: 32, height: 32, borderRadius: '0.5rem', cursor: 'pointer',
                      color: copySuccess === phase.name ? T.emerald : T.outlineVariant,
                      '&:hover': { color: T.outline, bgcolor: T.surfaceLow },
                    }}
                  >
                    <Icon name="content_copy" size={18} />
                  </Box>
                </Tooltip>

                <Box sx={{ width: 120 }}>
                  <LinearProgress
                    variant="determinate"
                    value={progress}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      backgroundColor: T.surfaceLow,
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: isComplete ? T.emerald : phase.color,
                        borderRadius: 4,
                      },
                    }}
                  />
                </Box>

                {isComplete ? (
                  <Typography component="span" sx={{ ...stitchPill('#166534', '#dcfce7'), display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                    <Icon name="check_circle" size={14} /> Voltooid
                  </Typography>
                ) : (
                  <Typography component="span" sx={stitchPill(T.outline, T.surfaceLow)}>
                    {`${Math.round(progress)}%`}
                  </Typography>
                )}
              </Box>
            </Box>

            {/* Deliverables within this phase - Collapsible */}
            <Collapse in={isExpanded}>
              <Box sx={{ p: 2 }}>
              {/* Skip deliverable grouping for Postproductie - show flat list */}
              {hasMultipleDeliverables && !phase.skipDeliverableGrouping ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {sortedDeliverables.map(([deliverableName, deliverableTasks]) => {
                    const delCompleted = getCompletedCount(deliverableTasks);
                    const delTotal = deliverableTasks.length;
                    const delProgress = delTotal > 0 ? (delCompleted / delTotal) * 100 : 0;

                    return (
                      <Box key={deliverableName}>
                        {/* Deliverable sub-header */}
                        <Box sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          mb: 1,
                          pb: 1,
                          borderBottom: `1px dashed ${T.surfaceHigh}`,
                        }}>
                          <Icon name="movie" size={16} sx={{ color: T.outlineVariant }} />
                          <Typography variant="body2" sx={{ fontFamily: T.fontBody, fontWeight: 600, color: T.text, flex: 1 }}>
                            {deliverableName}
                          </Typography>
                          <Typography component="span" sx={stitchPill(
                            delProgress === 100 ? '#166534' : T.outline,
                            delProgress === 100 ? '#dcfce7' : T.surfaceLow
                          )}>
                            {`${delCompleted}/${delTotal}`}
                          </Typography>
                        </Box>

                        {/* Tasks for this deliverable — sorted by start date */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, pl: 1 }}>
                          {[...deliverableTasks].sort((a, b) => {
                            const dateA = a.fields?.StartDateUitvoerder || a.fields?.Deadline || '';
                            const dateB = b.fields?.StartDateUitvoerder || b.fields?.Deadline || '';
                            return dateA.localeCompare(dateB);
                          }).map(task => (
                            <TaskItem key={task.id} task={task} compact onClick={() => onTaskClick?.(task)} onToggleComplete={onToggleComplete} />
                          ))}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              ) : (
                // Simple list sorted by start date (for Postproductie and single deliverable groups)
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {[...phase.tasks].sort((a, b) => {
                    const dateA = a.fields?.StartDateUitvoerder || a.fields?.Deadline || '';
                    const dateB = b.fields?.StartDateUitvoerder || b.fields?.Deadline || '';
                    return dateA.localeCompare(dateB);
                  }).map(task => (
                    <TaskItem key={task.id} task={task} compact onClick={() => onTaskClick?.(task)} onToggleComplete={onToggleComplete} />
                  ))}
                </Box>
              )}
              </Box>
            </Collapse>
          </Box>
        );
      })}
    </Box>
  );
}

export default PhaseTasksView;
